import { FunctionComponent, useEffect, useState } from 'react'
import { ClosingDay } from '@/types/ClosingDays'
import { EventDay } from '@/types/EventDay'
import {
    Absence,
    OccasionalSlot,
    OccasionalSlotsPlaces,
    RegularCancellationRequest,
    RegularSlot,
    RequestSlot
} from '@/types/Slots'
import { Work } from '@/types/work'
import ShowRegularSlotDialog from './showRegularSlot/showRegularSlotDialog'
import { Daycare } from '@/types/Daycare'
import ShowAbsenceDialog from './showAbsence/showAbsenceDialog'
import ShowStaffWorkDialog from './showStaffWork/showStaffWorkDialog'
import ShowRequestSlotDialogComponent from './showRequestSlot/showRequestSlotDialog'
import ShowRegularRequestDialog from './showRegularRequest/showRegularRequestDialog'
import ShowOccasionalSlotDialogComponent from './showOccasionalSlot/showOccasionalSlotDialog'
import { ChildWithParents } from '@/types/Child'
import { CalendarEventType, EventCalendar } from '@/types/Calendar'
import { Parent } from '@/types/Parent'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { StaffWithLinks } from '@/types/Staff'
import { TaskNote } from '@/types/TaskNote.ts'
import { useDialog } from '@/hooks/useDialog/useDialog'

interface ShowSlotProps {
    open: boolean
    handleClose: () => void
    selectedEvent: EventCalendar | null
    daycare: Daycare
    refetchData: () => void
    childs: ChildWithParents[]
    staffsListData: Array<StaffWithLinks>
    calendarEvents: EventCalendar[]
    places: OccasionalSlotsPlaces[]
    isParent: boolean
    parent?: Parent
    works: Work[]
    canEditRegular: boolean
    canEditStaffWorks: boolean
    lastRegularSlotDate: string
}

const ShowSlot: FunctionComponent<ShowSlotProps> = ({
    open,
    handleClose,
    selectedEvent,
    daycare,
    refetchData,
    childs,
    calendarEvents,
    places,
    isParent,
    parent,
    staffsListData,
    works,
    canEditRegular,
    canEditStaffWorks,
    lastRegularSlotDate
}: ShowSlotProps) => {
    const navigate: NavigateFunction = useNavigate()

    const [startDate, setStartDate] = useState<Date | null>(null)

    const [regularSlots, setRegularSlots] = useState<RegularSlot[]>([])
    const [regularRequests, setRegularRequests] = useState<RegularCancellationRequest[]>([])
    const [absences, setAbsences] = useState<Absence[]>([])
    const [staffWork, setStaffWork] = useState<Work | null>(null)
    const [requestSlots, setRequestSlots] = useState<RequestSlot[]>([])
    const [occasionalSlots, setOccasionalSlots] = useState<OccasionalSlot[]>([])

    const [closingDays, setClosingDays] = useState<ClosingDay[]>([])

    useEffect(() => {
        const closingDays: ClosingDay[] = []
        calendarEvents.forEach((event) => {
            if (event.type === CalendarEventType.CLOSING_DAY) {
                closingDays.push(event.slots as ClosingDay)
            }
        })
        setClosingDays(closingDays)
    }, [calendarEvents])

    useEffect(() => {
        if (selectedEvent) {
            switch (selectedEvent.type) {
                case CalendarEventType.CLOSING_DAY:
                    reset()
                    navigate(
                        `/events?closingDay=${(selectedEvent.slots as ClosingDay).id}` +
                            `&fromDate=${(selectedEvent.slots as ClosingDay).startDate}` +
                            `&toDate=${(selectedEvent.slots as ClosingDay).endDate}`
                    )
                    break
                case CalendarEventType.EVENT:
                    reset()
                    navigate(
                        `/events?event=${(selectedEvent.slots as EventDay).id}` +
                            `&fromDate=${(selectedEvent.slots as EventDay).date}` +
                            `&toDate=${(selectedEvent.slots as EventDay).date}`
                    )
                    break
                case CalendarEventType.TASK_NOTE:
                    reset()
                    navigate(
                        `/events?taskNote=${(selectedEvent.slots as TaskNote).id}` +
                            `&fromDate=${(selectedEvent.slots as TaskNote).date}` +
                            `&toDate=${(selectedEvent.slots as TaskNote).date}`
                    )
                    break
                case CalendarEventType.REGULAR:
                    reset()
                    setStartDate(selectedEvent.start)
                    setRegularSlots(selectedEvent.slots as RegularSlot[])
                    break
                case CalendarEventType.ABSENCE:
                    reset()
                    setAbsences(selectedEvent.slots as Absence[])
                    break
                case CalendarEventType.REGULAR_REQUEST:
                    reset()
                    setRegularRequests(selectedEvent.slots as RegularCancellationRequest[])
                    break
                case CalendarEventType.IN_SECTION:
                case CalendarEventType.NOT_IN_SECTION:
                case CalendarEventType.STAFF_HOLIDAY:
                case CalendarEventType.STAFF_TRAINING:
                    reset()
                    setStaffWork(selectedEvent.slots as Work)
                    break
                case CalendarEventType.REQUEST:
                    reset()
                    setRequestSlots(selectedEvent.slots as RequestSlot[])
                    break
                case CalendarEventType.AVAILABLE:
                case CalendarEventType.SCHEDULED:
                case CalendarEventType.VALIDATED:
                case CalendarEventType.REMAINDER_AVAILABLE:
                case CalendarEventType.EXPIRED:
                    reset()
                    setOccasionalSlots(selectedEvent.slots as OccasionalSlot[])
                    break
                default:
                    break
            }
        }
    }, [selectedEvent])

    const { dialog: ShowRequestSlotDialog, openDialog: openShowRequestSlotDialog } = useDialog({
        dialogTitle: 'requestSlot',
        Component: ShowRequestSlotDialogComponent,
        props: (handleClose) => ({
            refetchData,
            requestSlots,
            handleClose
        }),
        hideDialogClose: true,
        onClose: handleCloseAndReset
    })

    const { dialog: ShowOccasionalSlotsDialog, openDialog: openShowOccasionalSlotsDialog } = useDialog({
        dialogTitle: true,
        Component: ShowOccasionalSlotDialogComponent,
        props: (handleClose) => ({
            handleClose,
            occasionalSlotType: selectedEvent!.type,
            occasionalSlots,
            refetchData,
            places,
            calendarEvents,
            childs,
            isParent,
            parent
        }),
        hideDialogClose: true,
        onClose: handleCloseAndReset
    })

    useEffect(() => {
        if (requestSlots.length > 0) openShowRequestSlotDialog()
    }, [requestSlots])

    useEffect(() => {
        if (occasionalSlots.length > 0) openShowOccasionalSlotsDialog()
    }, [occasionalSlots])

    function reset(): void {
        setRegularSlots([])
        setAbsences([])
        setRegularRequests([])
        setStaffWork(null)
        setRequestSlots([])
        setOccasionalSlots([])
    }

    function handleCloseAndReset(): void {
        reset()
        handleClose()
    }

    return (
        <>
            {selectedEvent && (
                <>
                    {regularSlots.length > 0 && (
                        <ShowRegularSlotDialog
                            open={regularSlots.length > 0}
                            handleClose={handleCloseAndReset}
                            startDate={startDate}
                            regularSlots={regularSlots}
                            daycare={daycare}
                            places={places}
                            isParent={isParent}
                            refetchData={refetchData}
                            canEditRegular={canEditRegular}
                            lastRegularSlotDate={lastRegularSlotDate}
                            closingDays={closingDays}
                        />
                    )}
                    {regularRequests.length > 0 && (
                        <ShowRegularRequestDialog
                            open={regularRequests.length > 0}
                            handleClose={handleCloseAndReset}
                            regularRequests={regularRequests}
                            daycare={daycare}
                            isParent={isParent}
                            refetchData={refetchData}
                            childs={childs}
                            canEditRegular={canEditRegular}
                            places={places}
                        />
                    )}
                    {absences.length > 0 && (
                        <ShowAbsenceDialog
                            open={absences.length > 0}
                            handleClose={handleCloseAndReset}
                            absences={absences}
                            daycare={daycare}
                            refetchData={refetchData}
                            canEditRegular={canEditRegular}
                        />
                    )}
                    {staffWork && (
                        <ShowStaffWorkDialog
                            open={open}
                            handleClose={handleCloseAndReset}
                            staffWork={staffWork}
                            daycare={daycare}
                            refetchData={refetchData}
                            staffsListData={staffsListData}
                            works={works}
                            canEditStaffWorks={canEditStaffWorks}
                        />
                    )}
                    {ShowRequestSlotDialog}
                    {ShowOccasionalSlotsDialog}
                </>
            )}
        </>
    )
}

export default ShowSlot
